import { PRNG } from './prng.ts';

export interface ChallengeData {
  seed: string;
  creator: string;
  score: number;
  maxCombo: number;
  ver: number;
  ts?: number;
  sig?: string;
}

export class ChallengeSystem {
  private static readonly MAX_SCORE_CEILING = 60000;
  private static readonly SALT = 0xa481d9;

  /**
   * Generates a lightweight checksum for anti-tamper validation
   */
  public static generateSignature(seed: string, creator: string, score: number, combo: number): string {
    const raw = `${seed}:${creator.toLowerCase()}:${Math.round(score)}:${combo}:${this.SALT}`;
    return (PRNG.hashString(raw) >>> 0).toString(36);
  }

  /**
   * Encodes challenge data to a URL-safe Base64 payload
   */
  public static encodeChallenge(data: ChallengeData): string {
    const creator = (data.creator || 'Guest').trim().slice(0, 16);
    const score = Math.min(this.MAX_SCORE_CEILING, Math.max(0, Math.round(data.score)));
    const maxCombo = Math.max(0, Math.round(data.maxCombo || 0));
    const seed = data.seed || PRNG.generateSeedCode();
    const ver = data.ver || 2;
    const ts = data.ts || Date.now();
    const sig = this.generateSignature(seed, creator, score, maxCombo);

    const payload = {
      s: seed,
      c: creator,
      sc: score,
      mc: maxCombo,
      v: ver,
      t: ts,
      h: sig,
    };

    const jsonStr = JSON.stringify(payload);
    return btoa(encodeURIComponent(jsonStr))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Decodes and validates a URL-safe Base64 challenge payload
   */
  public static decodeChallenge(token: string): ChallengeData | null {
    if (!token || typeof token !== 'string') return null;

    try {
      let base64 = token.replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) {
        base64 += '=';
      }
      const jsonStr = decodeURIComponent(atob(base64));
      const obj = JSON.parse(jsonStr);

      if (!obj || typeof obj.s !== 'string' || typeof obj.sc !== 'number') {
        return null;
      }

      const score = Math.round(obj.sc);
      // Reject impossible score tampering
      if (score < 0 || score > this.MAX_SCORE_CEILING) {
        return null;
      }

      const seed = obj.s.trim();
      const creator = typeof obj.c === 'string' ? obj.c.trim().slice(0, 16) : 'Anonymous';
      const maxCombo = typeof obj.mc === 'number' ? Math.max(0, Math.round(obj.mc)) : 0;
      const ver = typeof obj.v === 'number' ? obj.v : 1;
      const ts = typeof obj.t === 'number' ? obj.t : undefined;
      const sig = typeof obj.h === 'string' ? obj.h : undefined;

      return {
        seed,
        creator: creator || 'Anonymous',
        score,
        maxCombo,
        ver,
        ts,
        sig,
      };
    } catch {
      return null;
    }
  }

  /**
   * Inspects current window URL for active challenge
   */
  public static getActiveChallengeFromURL(): ChallengeData | null {
    if (typeof window === 'undefined') return null;
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('c');
      if (!token) return null;
      return this.decodeChallenge(token);
    } catch {
      return null;
    }
  }

  /**
   * Checks if URL contains a daily challenge flag
   */
  public static isDailyChallengeURL(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('daily') === '1';
    } catch {
      return false;
    }
  }

  /**
   * Builds a complete shareable challenge URL
   */
  public static buildChallengeURL(data: ChallengeData): string {
    const token = this.encodeChallenge(data);
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : 'https://outaura.pages.dev';
    const pathname = typeof window !== 'undefined' && window.location ? window.location.pathname : '/';
    return `${origin}${pathname}?c=${token}`;
  }

  /**
   * Generates deterministic seed for the Daily Lock In based on YYYY-MM-DD
   */
  public static getDailySeed(date: Date = new Date()): string {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `DAILY-${y}-${m}-${d}`;
  }

  /**
   * Triggers native Web Share or falls back to Clipboard copy
   */
  public static async shareChallenge(data: ChallengeData): Promise<{ success: boolean; copied: boolean }> {
    const url = this.buildChallengeURL(data);
    const text = `I scored ${data.score.toLocaleString()} (${data.maxCombo}x combo) in OUTAURA: LOCK IN. Can you OUTAURA me? 💀\nPlay: ${url}`;

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: 'OUTAURA: LOCK IN Challenge',
          text,
          url,
        });
        return { success: true, copied: false };
      } catch {
        // Fallback to clipboard if dismissed or failed
      }
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        return { success: true, copied: true };
      } catch {
        return { success: false, copied: false };
      }
    }

    return { success: false, copied: false };
  }
}
